import { storeWrapper } from '../storeWrapper'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { storeTokens, removeTokens } from '@repo/api/apiClient'
import { mixpanelIdentifyUser, mixpanelUnidentifyUser } from '@repo/app-assets/Mixpanel'
import { performStoreLifecycleAction } from '@repo/store/storeWrapper'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { getAppInsightsInstance } from '@repo/app-assets/ApplicationInsights'
import {
  ospreyLoginSchema,
  ospreyRequestPasswordResetSchema,
  ospreyCompletePasswordResetSchema
} from '@repo/validation/schema_ospreyAPI'
import { debounce } from '@repo/utils/debounce'

const initialState = {
  isLoggedIn: false,
  iframeIsLoggedIn: false,
  loginPageLoading: false,
  emailAddress: '',
  userId: '',
  roleId: null,
  feeEarnerId: '',
  feeEarnerDescription: '',
  isSupervisor: false,
  defaultKpiId: null,
  minimumPasswordLength: 8,
  deepLink: null,
  hasGraphApiTokens: false,
  hasGraphApiSubscriptions: false,
  graphApiEmailAddress: null
}

export const useAuthenticationStore = storeWrapper('useAuthenticationStore', (set, get) => ({
  ...initialState,
  login: async (params) => {
    const { username, password, makeApiCall, twoFactorAuthCode } = params
    const { isOutlookAddin } = useGlobalSettingsStore.getState()
    const { companyCode, databaseSuffix } = useGlobalSettingsStore.getState().getDomainDetails()

    const payload = {
      companyCode,
      databaseSuffix,
      userIdOrEmailAddress: username,
      password
    }

    if (twoFactorAuthCode) payload.twoStepAuthenticationCode = twoFactorAuthCode.trim()

    const data = await ospreyAPI({
      service: 'security',
      endpoint: isOutlookAddin ? 'User/OutlookAuthenticate' : 'User/Authenticate',
      method: 'POST',
      data: payload,
      schema: ospreyLoginSchema,
      makeApiCall,
      withCredentials: true,
      suppressErrorToast: true
    })

    if (!data.errorMessage) {
      storeTokens({ params: data, tokenHasBeenRefreshed: false })

      const userId = data.userId
      set({ isLoggedIn: true, userId })
      const appInsights = getAppInsightsInstance()
      if (appInsights)
        appInsights.setAuthenticatedUserContext(userId, `${companyCode.toLowerCase()}/${databaseSuffix.toLowerCase()}`)
    }

    return data
  },
  setIsLoggedIn: (isLoggedIn) => set({ isLoggedIn }),
  getProfile: async (params) => {
    const { makeApiCall } = params || {}

    const {
      userId,
      roleId,
      emailAddress,
      feeEarnerId,
      feeEarnerDescription,
      isSupervisor,
      defaultKpiId,
      hasGraphApiTokens
    } = await ospreyAPI({
      service: 'security',
      endpoint: 'User/GetAbbreviatedCurrentUser',
      makeApiCall,
      suppressErrorToast: true
    })

    set({
      userId,
      roleId,
      emailAddress,
      feeEarnerId,
      feeEarnerDescription,
      isSupervisor,
      defaultKpiId,
      hasGraphApiTokens
    })

    const hasGraphApiSubscriptions = await get().fetchHasGraphApiSubscriptions()

    const { companyCode, databaseSuffix } = useGlobalSettingsStore.getState().getDomainDetails()
    mixpanelIdentifyUser({ userId, companyCode, databaseSuffix, emailAddress, isSupervisor, roleId, defaultKpiId })

    return {
      userId,
      roleId,
      emailAddress,
      feeEarnerId,
      feeEarnerDescription,
      isSupervisor,
      defaultKpiId,
      hasGraphApiTokens,
      hasGraphApiSubscriptions
    }
  },
  fetchHasGraphApiSubscriptions: async () => {
    const { useMicrosoftEmail, companyCanUseMicrosoftEmail } = useGlobalSettingsStore.getState()
    const { hasGraphApiTokens } = get()

    let hasGraphApiSubscriptions = false

    if (hasGraphApiTokens && useMicrosoftEmail === true && companyCanUseMicrosoftEmail === true) {
      try {
        hasGraphApiSubscriptions = await ospreyAPI({
          service: 'emails',
          endpoint: 'GraphApi/HasEmailSubscriptionByCurrentUser',
          suppressErrorToast: true
        })
      } catch (err) {
        // temporarily swallow this error until permissions are fixed
        console.error('Error getting email subscriptions:', err)
      }
    }

    const newValue = hasGraphApiSubscriptions?.data === false ? false : hasGraphApiSubscriptions
    set({ hasGraphApiSubscriptions: newValue })
    return newValue
  },
  fetchEmailSubscriptionEmailAddressByCurrentUser: async () => {
    const {hasGraphApiTokens, hasGraphApiSubscriptions, graphApiEmailAddress} = get()

    if (
      hasGraphApiTokens !== true ||
      hasGraphApiSubscriptions !== true
    ) {
      if (graphApiEmailAddress) set({ graphApiEmailAddress: null })
      return
    }

    return new Promise((resolve, reject) => {
      debounce({
        id: 'fetchEmailSubscriptionEmailAddressByCurrentUser',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              service: 'emails',
              method: 'GET',
              endpoint: '/GraphApi/GetEmailSubscriptionEmailAddressByCurrentUser'
            })
            set({ graphApiEmailAddress: response })
            resolve(response)
          } catch (err) {
            console.log('Error fetching GraphApi email address:', err)
            reject(err)
          }
        }
      })
    })
  },
  setIframeIsLoggedIn: (iframeIsLoggedIn) => set({ iframeIsLoggedIn }),
  setLoginPageLoading: (loginPageLoading) => set({ loginPageLoading }),
  requestPasswordReset: async ({ userIdOrEmailAddress, makeApiCall }) => {
    const { companyCode, databaseSuffix } = useGlobalSettingsStore.getState().getDomainDetails()

    const data = await ospreyAPI({
      service: 'security',
      endpoint: 'User/PasswordResetRequest',
      method: 'POST',
      data: {
        companyCode,
        databaseSuffix,
        userIdOrEmailAddress
      },
      schema: ospreyRequestPasswordResetSchema,
      makeApiCall
    })

    return data
  },
  completePasswordReset: async ({ passwordResetToken, userId, newPassword, makeApiCall }) => {
    const payload = {
      passwordResetToken,
      newPassword
    }

    if (userId) payload.userId = userId

    const data = await ospreyAPI({
      service: 'security',
      endpoint: 'User/PasswordResetUpdate',
      method: 'POST',
      data: payload,
      schema: ospreyCompletePasswordResetSchema,
      makeApiCall
    })

    return data
  },
  getMinimumPasswordLength: async (params) => {
    const { companyCode, databaseSuffix } = useGlobalSettingsStore.getState().getDomainDetails()
    const { makeApiCall } = params || {}

    const minimumPasswordLength = await ospreyAPI({
      service: 'security',
      endpoint: 'User/GetMinimumPasswordLength',
      data: { companyCode, databaseSuffix },
      makeApiCall
    })

    set({ minimumPasswordLength })
  },
  passAuthTokenToOsprey1: ({ authToken, baseURL }) => {
    try {
      // using fetch as we don't need all the apiCall features
      fetch(`${baseURL}/services/Api.asmx/SetAuthenticationToken?authenticationToken=${authToken}`, {
        method: 'GET'
      })
    } catch (err) {
      console.error('Error passing auth token to Osprey 1:', err)
    }
  },
  getFreshOsprey1Cookies: async () => {
    // calling this endpoint will force Osprey 1 to send the latest msAccessToken cookie to the browser
    try {
      // using fetch as we don't need all the apiCall features
      const { baseURL } = useGlobalSettingsStore.getState()
      await fetch(`${baseURL}/matterpage.aspx?Area=1000&Flag=1200`, {
        method: 'GET'
      })

      return Promise.resolve()
    } catch (err) {
      console.error('Error getting fresh cookies from Osprey 1:', err)
      throw err
    }
  },
  logout: async () => {
    removeTokens()
    performStoreLifecycleAction({ action: 'reset' })
    mixpanelUnidentifyUser()
    await ospreyAPI({
      service: 'security',
      endpoint: 'User/DeleteRefreshTokenCookie',
      method: 'POST',
      withCredentials: true
    })
  },
  setDeepLink: (deepLink) => set({ deepLink }),
  reset: () => {
    const userId = window.Office ? get().userId : ''
    set({ ...initialState, userId })
  }
}))
